import { Badge } from '@mantine/core';
import type { FilterDef, ReportColumn, StreamedSourceConfig } from '../types';

/**
 * Columns that only mean something after the missing-data run. Ticking any of these (or their B
 * twins, which follow automatically) asks the user to press Start before Export is enabled.
 */
export const REGISTRATION_ENRICHED_KEYS = new Set([
  'firstName', 'surname', 'email', 'phone', 'gender', 'dateOfBirth', 'missingFields', 'filledFrom',
]);

/** Person B duplicates. Same order as the Person A columns; no checkbox of their own. */
const twinColumns: ReportColumn[] = [
  { key: 'firstNameB', label: 'First Name (B)', category: 'Person' },
  { key: 'surnameB', label: 'Surname (B)', category: 'Person' },
  { key: 'emailB', label: 'Email (B)', category: 'Person' },
  { key: 'phoneB', label: 'Phone (B)', category: 'Person' },
  { key: 'genderB', label: 'Gender (B)', category: 'Person' },
  { key: 'dateOfBirthB', label: 'Date of Birth (B)', category: 'Person', type: 'date' },
];

export const registration: StreamedSourceConfig = {
  mode: 'streamed',
  label: 'Registration',
  plural: 'Registrations',
  primaryKey: 'registrationID',
  groupField: 'participantCount',
  columns: [
    { key: 'registrationID', label: 'ID', category: 'Registration' },
    { key: 'reference', label: 'Reference', category: 'Registration', defaultVisible: true },
    { key: 'description', label: 'Description', category: 'Registration', defaultVisible: true },
    { key: 'courseName', label: 'Course', category: 'Registration', defaultVisible: true },
    { key: 'periodDisplay', label: 'Date', category: 'Registration', defaultVisible: true },
    { key: 'registeredDate', label: 'Registered', category: 'Registration', type: 'date', defaultVisible: true },
    { key: 'statusName', label: 'Status', category: 'Registration', defaultVisible: true },
    { key: 'locationName', label: 'Location', category: 'Church', defaultVisible: true },
    { key: 'leadFacilitatorName', label: 'Facilitator', category: 'Registration' },
    { key: 'sessionCount', label: 'Sessions', category: 'Registration', type: 'number' },
    {
      key: 'participantCount',
      label: 'Participants',
      category: 'Registration',
      type: 'number',
      defaultVisible: true,
      render: (value, _row, ctx) => {
        const n = Number(value ?? 0);
        if (!n) return '—';
        if (n <= 1) return String(n);
        const colour = ctx.tint === 'b' ? 'teal' : 'blue';
        return (
          <Badge variant="filled" color={colour} size="sm">
            {`${n} people`}
          </Badge>
        );
      },
    },
    { key: 'mainPersonAffiliation', label: 'Affiliation', category: 'Person' },
    // The people attending (main participant = Person A; a second participant = the B twins).
    { key: 'firstName', label: 'First Name', category: 'Person', twinKey: 'firstNameB' },
    { key: 'surname', label: 'Surname', category: 'Person', twinKey: 'surnameB' },
    { key: 'email', label: 'Email', category: 'Person', twinKey: 'emailB' },
    { key: 'phone', label: 'Phone', category: 'Person', twinKey: 'phoneB' },
    { key: 'gender', label: 'Gender', category: 'Person', twinKey: 'genderB' },
    { key: 'dateOfBirth', label: 'Date of Birth', category: 'Person', type: 'date', twinKey: 'dateOfBirthB' },
    { key: 'startDateTime', label: 'Start', category: 'Registration', type: 'datetime' },
    { key: 'endDateTime', label: 'End', category: 'Registration', type: 'datetime' },
    { key: 'missingFields', label: 'Missing fields', category: 'Data quality', trailing: true },
    { key: 'filledFrom', label: 'Filled from', category: 'Data quality', trailing: true },
  ],
  twinColumns,
  filters: [
    { key: 'status', field: 'statusID', label: 'Status', type: 'select', placeholder: 'All statuses', runtimeOptions: true },
    { key: 'course', field: 'courseID', label: 'Course', type: 'select', placeholder: 'All courses', runtimeOptions: true },
    {
      key: 'date',
      field: 'dateID',
      label: 'Course Date',
      type: 'select',
      placeholder: 'Pick a course first',
      runtimeOptions: true,
      dependsOn: {
        key: 'course',
        placeholder: 'Pick a course first',
        options: (_parent: string) => [],
      },
    },
    { key: 'facilitator', field: 'leadFacilitatorUserId', label: 'Facilitator', type: 'select', placeholder: 'All facilitators', runtimeOptions: true },
    { key: 'location', field: 'locationID', label: 'Location', type: 'select', placeholder: 'All locations', runtimeOptions: true },
  ] satisfies FilterDef[],
};